import { View, Text, TouchableOpacity, StyleSheet, Platform, Image, ImageStyle } from 'react-native';
import { useEffect, useState } from 'react';
import { useRouter, usePathname, Link, useGlobalSearchParams } from 'expo-router';
import { useSession } from '../lib/SessionContext';
import { useAuth } from '../lib/AuthContext';
import NavigationMenu from './NavigationMenu';
import HeaderBrandTitle from './HeaderBrandTitle';
import { MaterialIcons } from '@expo/vector-icons';
import { signOutCurrentSession } from '../lib/supabase';
import { canAccessMainApp } from '../utils/authHelpers';
import colors from '../lib/colors';
import { responsive } from '../lib/responsive';
import { hasEverLoggedInHint } from '../utils/firstTimeUser';
import { getCurrentRedirectTo, inferFunnelContextFromRoute } from '../lib/funnelContext';
import { buildAccountSettingsPath } from '../lib/accountRoutes';
import {
    buildCanonicalDashboardPath,
    DASHBOARD_ACTIVATION_ROUTE_PATTERN,
    DASHBOARD_DELIVERY_ROUTE_PATTERN,
} from '../lib/dashboardRoutes';
import PreviewTrialBanner from './billing/PreviewTrialBanner';
import { Trans } from '@lingui/react/macro';
import { boundarySafeHref } from '../lib/i18n/publicWeb';
import LanguageSelector from './i18n/LanguageSelector';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';
import { documentationHref } from '../lib/i18n/documentation';

interface HeaderProps {
    hidePreviewTrialBanner?: boolean;
}

export default function Header({ hidePreviewTrialBanner = false }: HeaderProps) {
    const router = useRouter();
    const { openAuth } = useAuth();
    const { messageLocale } = useAppLocale();
    const pathname = usePathname();
    const searchParams = useGlobalSearchParams();
    const [menuVisible, setMenuVisible] = useState(false);
    const [accountDropdownVisible, setAccountDropdownVisible] = useState(false);
    const [mounted, setMounted] = useState(false);
    const { session, isReturningUser } = useSession();
    const isAnonymousSession = Boolean(session) && !canAccessMainApp(session);
    const canAccessApp = canAccessMainApp(session);
    const isDeliveryPath = DASHBOARD_DELIVERY_ROUTE_PATTERN.test(pathname || '');
    const isActivationPath = DASHBOARD_ACTIVATION_ROUTE_PATTERN.test(pathname || '');
    const hideAuthAction = isAnonymousSession && (isDeliveryPath || isActivationPath);
    const settingsHref = boundarySafeHref(buildAccountSettingsPath());
    const dashboardHref = boundarySafeHref(buildCanonicalDashboardPath());
    const inboxHref = boundarySafeHref(buildCanonicalDashboardPath('/inbox'));
    const isDashboardRoute = pathname === '/digests' || pathname === '/dashboard';
    const isInboxRoute = pathname === buildCanonicalDashboardPath('/inbox');
    const isProductRoute = pathname === '/product';
    const isAgentsRoute = pathname === '/agents';
    const isPricingRoute = pathname === '/pricing';

    // Only render auth UI after hydration to prevent flash
    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (Platform.OS === 'web') {
            console.log('Header pathname:', pathname);
        }
    }, [pathname]);

    const handleLogoPress = () => {
        // Only navigate if not already on home page
        if (pathname !== '/') {
            router.push('/');
        }
    };

    const toggleMenu = () => {
        setMenuVisible(!menuVisible);
        // Close account dropdown when opening mobile menu
        setAccountDropdownVisible(false);
    };

    const handleClickOutside = () => {
        setAccountDropdownVisible(false);
    };

    const handleAuthRequired = () => {
        const mode: 'signin' | 'signup' = session
            ? 'signup'
            : (isReturningUser || hasEverLoggedInHint())
                ? 'signin'
                : 'signup';

        openAuth({
            mode,
            redirectTo: getCurrentRedirectTo(pathname || '/'),
            funnelContext: inferFunnelContextFromRoute(
                pathname || '/',
                searchParams as Record<string, string | string[] | undefined>
            ) || undefined,
        });
    };


    const handleAuth = async () => {
        if (canAccessMainApp(session)) {
            // Properly authenticated user - sign out
            console.log('🚪 [Header] Starting logout process...');
            try {
                const { error } = await signOutCurrentSession();
                if (error) {
                    // If session is already missing, treat as successful logout
                    if (error.name === 'AuthSessionMissingError') {
                        console.log('🚪 [Header] Session already missing, treating as logged out');
                    } else {
                        console.error('🚪 [Header] Error during sign out:', error);
                    }
                } else {
                    console.log('✅ [Header] Successfully signed out');
                }
                // Close dropdown after logout (whether session was missing or not)
                setAccountDropdownVisible(false);
                router.replace('/');
            } catch (err) {
                console.error('🚪 [Header] Exception during logout:', err);
                // Still close dropdown on error
                setAccountDropdownVisible(false);
                router.replace('/');
            }
        } else {
            // Anonymous or no session - show auth modal
            handleAuthRequired();
        }
    };

    return (
        <>
            <View style={[styles.header, accountDropdownVisible && { zIndex: 100 }]}>
                <TouchableOpacity
                    style={styles.brandingContainer}
                    onPress={handleLogoPress}
                    testID="header-logo-button"
                >
                    <Image
                        source={require('../assets/logo.png')}
                        style={styles.logo as ImageStyle}
                        resizeMode="contain"
                        {...responsive('header-logo')}
                    />
                    <View style={styles.titleContainer}>
                        <HeaderBrandTitle />
                        <Text
                            style={styles.subtitle}
                            numberOfLines={1}
                            {...(pathname === '/'
                                ? { accessibilityRole: "header", ...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 }) }
                                : { accessibilityRole: "text" }
                            )}
                            {...responsive('header-subtitle')}
                        ><Trans id="header.highSignalResearchFeedsAndMorningBriefs" comment="UI copy in components/Header.tsx">
                            High-signal research feeds{' '}
                            <Text {...responsive('header-subtitle-expanded')}>
                                and morning briefs
                            </Text>
                        </Trans></Text>
                    </View>
                </TouchableOpacity>

            {/* Inline navigation for wider screens - CSS controls visibility */}
            <View
                style={[styles.inlineNav, accountDropdownVisible && { zIndex: 10 }]}
                {...responsive('header-inline-nav')}
            >
                {canAccessApp ? (
                    <>
                        <Link href={dashboardHref} asChild>
                            <TouchableOpacity
                                style={styles.navItem}
                                activeOpacity={0.7}
                            >
                                <Text style={[styles.navItemText, isDashboardRoute && styles.navItemTextActive]}><Trans id="header.dashboard" comment="UI copy in components/Header.tsx">
                                    Dashboard
                                </Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Link href={inboxHref} asChild>
                            <TouchableOpacity
                                style={styles.navItem}
                                activeOpacity={0.7}
                                testID="header-inbox-button"
                            >
                                <Text style={[styles.navItemText, isInboxRoute && styles.navItemTextActive]}><Trans id="header.inbox" comment="UI copy in components/Header.tsx">
                                    Inbox
                                </Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Link
                            href={documentationHref(messageLocale)}
                            target="_blank"
                            style={styles.navLinkText}
                        ><Trans id="header.docs" comment="UI copy in components/Header.tsx">
                            Docs
                        </Trans></Link>

                        {mounted && (
                            <View style={[styles.accountContainer, accountDropdownVisible && { zIndex: 10 }]}>
                                <TouchableOpacity
                                    style={styles.accountIconButton}
                                    onPress={() => setAccountDropdownVisible(!accountDropdownVisible)}
                                    activeOpacity={0.7}
                                    testID="header-account-menu-button"
                                >
                                    <MaterialIcons
                                        name="account-circle"
                                        size={32}
                                        color={colors.primaryGreenDark}
                                    />
                                </TouchableOpacity>

                                {/* Invisible overlay to close dropdown - inside container, before dropdown */}
                                {accountDropdownVisible && (
                                    <TouchableOpacity
                                        style={styles.dropdownOverlay}
                                        onPress={handleClickOutside}
                                        activeOpacity={1}
                                    />
                                )}

                                {accountDropdownVisible && (
                                    <View style={styles.accountDropdown}>
                                        <View style={styles.dropdownContent}>
                                            <Text style={styles.userEmail} numberOfLines={1}>
                                                {session?.user?.email || <Trans id="header.user" comment="Conditional UI copy in components/Header.tsx">User</Trans>}
                                            </Text>
                                            <Link href={settingsHref} asChild>
                                                <TouchableOpacity
                                                    style={styles.dropdownItem}
                                                    onPress={() => setAccountDropdownVisible(false)}
                                                    activeOpacity={0.7}
                                                    testID="header-settings-button"
                                                >
                                                    <Text style={styles.dropdownItemText}><Trans id="header.settings" comment="UI copy in components/Header.tsx">Settings</Trans></Text>
                                                </TouchableOpacity>
                                            </Link>
                                            <Link href="/ai-connections" asChild>
                                                <TouchableOpacity style={styles.dropdownItem} onPress={() => setAccountDropdownVisible(false)}>
                                                    <Text style={styles.dropdownItemText}><Trans>Connect AI clients</Trans></Text>
                                                </TouchableOpacity>
                                            </Link>
                                            <TouchableOpacity
                                                style={styles.dropdownItem}
                                                onPress={async () => {
                                                    console.log('🔐 [Header] Sign Out clicked');
                                                    // Don't close dropdown immediately - let handleAuth do it after success
                                                    await handleAuth();
                                                }}
                                                activeOpacity={0.7}
                                                testID="header-sign-out-button"
                                            >
                                                <Text style={styles.dropdownItemText}><Trans id="header.signOut" comment="UI copy in components/Header.tsx">Sign Out</Trans></Text>
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                )}
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        <Link href="/product" asChild>
                            <TouchableOpacity
                                style={styles.navItem}
                                activeOpacity={0.7}
                            >
                                <Text style={[styles.navItemText, isProductRoute && styles.navItemTextActive]}><Trans id="header.product" comment="UI copy in components/Header.tsx">
                                    Product
                                </Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Link href="/pricing" asChild>
                            <TouchableOpacity
                                style={styles.navItem}
                                activeOpacity={0.7}
                            >
                                <Text style={[styles.navItemText, isPricingRoute && styles.navItemTextActive]}><Trans id="header.pricing" comment="UI copy in components/Header.tsx">
                                    Pricing
                                </Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Link href="/research-briefs" asChild>
                            <TouchableOpacity style={styles.navItem} activeOpacity={0.7}>
                                <Text style={[styles.navItemText, pathname === '/research-briefs' && styles.navItemTextActive]}><Trans>Research briefs</Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Link href="/agents" asChild>
                            <TouchableOpacity
                                style={styles.navItem}
                                activeOpacity={0.7}
                            >
                                <Text style={[styles.navItemText, isAgentsRoute && styles.navItemTextActive]}><Trans id="header.agents" comment="UI copy in components/Header.tsx">
                                    Agents
                                </Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Link
                            href={documentationHref(messageLocale)}
                            target="_blank"
                            style={styles.navLinkText}
                        ><Trans id="header.docs" comment="UI copy in components/Header.tsx">
                            Docs
                        </Trans></Link>

                        {/* Only render auth UI after hydration to prevent flash */}
                        {mounted && !hideAuthAction && (
                            <TouchableOpacity
                                style={styles.navItem}
                                onPress={handleAuth}
                                activeOpacity={0.7}
                                testID="header-auth-button"
                            >
                                <Text style={styles.navItemText}><Trans id="header.signUp" comment="UI copy in components/Header.tsx">
                                    Sign up
                                </Trans></Text>
                            </TouchableOpacity>
                        )}
                    </>
                )}
                <LanguageSelector variant="header" />
            </View>

            {/* Hamburger menu for narrow screens - CSS controls visibility */}
            <TouchableOpacity
                style={styles.menuButton}
                onPress={toggleMenu}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                testID="header-menu-button"
                {...responsive('header-hamburger')}
            >
                <MaterialIcons
                    name="menu"
                    size={32}
                    color={colors.darkGreen}
                />
            </TouchableOpacity>


            {/* Navigation menu modal */}
            <NavigationMenu
                visible={menuVisible}
                onClose={() => setMenuVisible(false)}
                onAuthRequired={handleAuthRequired}
                hideAuthAction={hideAuthAction}
            />
            </View>
            {canAccessApp && !hidePreviewTrialBanner && <PreviewTrialBanner />}
        </>
    );
}

const styles = StyleSheet.create({
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: Platform.OS === 'web' ? 20 : 60,
        paddingBottom: 12,
        backgroundColor: "rgba(255,255,255,0.86)",
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
        // Keep web navigation popovers above the following page's stacking context.
        ...(Platform.OS === 'web' && { zIndex: 10 }),
    },
    brandingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
    },
    logo: {
        width: 80,
        height: 80,
        marginRight: 12,
    },
    titleContainer: {
        flex: 1,
        minWidth: 0,
        paddingRight: 8,
    },
    subtitle: {
        fontSize: 18,
        color: '#6b7280',
        lineHeight: 20,
        flexShrink: 1,
        ...(Platform.OS === 'web' && {
            whiteSpace: 'nowrap' as any,
        }),
    },
    menuButton: {
        marginTop: -4,
        marginRight: -4,
        paddingLeft: 6,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 40,
        minHeight: 40,
    },
    inlineNav: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 16,
        gap: 4,
        flexShrink: 0,
    },
    navItem: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            ':hover': {
                backgroundColor: '#f3f4f6',
            },
        }),
    },
    navItemText: {
        fontSize: 16,
        color: colors.primaryGreenDark,
        fontWeight: '500',
        ...(Platform.OS === 'web' && {
            whiteSpace: 'nowrap' as any,
        }),
    },
    navLinkText: {
        fontSize: 16,
        color: colors.primaryGreenDark,
        fontWeight: '500',
        paddingHorizontal: 12,
        paddingVertical: 8,
        ...(Platform.OS === 'web' && {
            textDecorationLine: 'none',
            whiteSpace: 'nowrap' as any,
        }),
    },
    navItemTextActive: {
        color: colors.primaryGreen,
    },
    accountContainer: {
        position: 'relative',
        marginLeft: 2,
    },
    accountIconButton: {
        padding: 8,
        borderRadius: 20,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            ':hover': {
                backgroundColor: '#f3f4f6',
            },
        }),
    },
    accountDropdown: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 8,
        backgroundColor: 'white',
        borderRadius: 8,
        elevation: 8,
        minWidth: 200,
        zIndex: 1000,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }),
    },
    dropdownContent: {
        padding: 8,
    },
    userEmail: {
        fontSize: 14,
        color: '#6b7280',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
        marginBottom: 4,
    },
    dropdownItem: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 6,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            ':hover': {
                backgroundColor: '#f9fafb',
            },
        }),
    },
    dropdownItemText: {
        fontSize: 14,
        color: colors.primaryGreenDark,
        fontWeight: '500',
    },
    dropdownOverlay: Platform.select({
        web: {
            position: 'fixed' as any,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw' as any,
            height: '100vh' as any,
            zIndex: 999,
        },
        default: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
        }
    }),
});
